"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CircleCheck, FileText, TrendingUp, Shield } from "lucide-react";
export default function TaxPlanningAndAdvisory() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const benefits = [{
    icon: FileText,
    title: "Strategic Tax Planning",
    description: "Proactive planning to minimize your tax liability and maximize savings throughout the year."
  }, {
    icon: TrendingUp,
    title: "Tax Efficiency",
    description: "Optimize your financial structure to take advantage of available tax reliefs and allowances."
  }, {
    icon: Shield,
    title: "Compliance Assurance",
    description: "Stay fully compliant with HMRC regulations while minimizing your tax burden legally."
  }];
  const keyServices = ["Personal and corporate tax planning", "Capital gains tax advisory", "Inheritance tax planning", "VAT planning and optimization", "Tax return preparation and filing", "HMRC enquiry support"];
  return <section id="taxplanningandadvisory" className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-4 max-w-7xl">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-500 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-[36px] md:text-[48px] leading-[1.2] font-bold tracking-[-0.2px] mb-4">
            Tax Planning & Advisory
          </h2>
          <p className="text-[16px] leading-[1.6] text-muted-foreground max-w-3xl mx-auto">
            Expert tax planning and advisory services designed to help you navigate complex tax regulations, reduce your tax liability, and ensure full compliance with HMRC requirements.
          </p>
        </div>

        <div ref={cardsRef} className={`grid md:grid-cols-3 gap-6 mb-16 transition-all duration-500 delay-100 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <Card key={i} data-index={i} className="border border-border shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-200">
                <CardHeader>
                  <div className="w-12 h-12 rounded-[var(--radius-sm)] bg-primary-light flex items-center justify-center mb-4">
                    <Icon className="size-6 text-primary" />
                  </div>
                  <CardTitle className="text-[22px] leading-[1.5] font-semibold">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-[16px] leading-[1.6]">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div className={`transition-all duration-500 delay-200 ${cardsVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <h3 className="text-[28px] leading-[1.4] font-semibold tracking-[-0.2px] mb-6">
              Comprehensive Tax Services
            </h3>
            <p className="text-[16px] leading-[1.6] text-muted-foreground mb-6">
              Our experienced tax advisors work closely with you to develop tailored tax strategies that align with your financial goals. We stay up-to-date with the latest tax legislation to ensure you benefit from every available opportunity.
            </p>
            <ul className="space-y-3">
              {keyServices.map((service, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="size-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-[16px] leading-[1.6]">{service}</span>
                </li>)}
            </ul>
          </div>

          <div className={`transition-all duration-500 delay-300 ${cardsVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <Card className="bg-primary-light border-primary/20">
              <CardHeader>
                <CardTitle className="text-[22px] leading-[1.5] font-semibold">
                  Why Choose Our Tax Advisory?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-[16px] leading-[1.6]">
                  With decades of combined experience, our team provides proactive tax planning that goes beyond simple compliance. We identify opportunities for tax savings and help you make informed decisions.
                </p>
                <p className="text-[16px] leading-[1.6]">
                  Our approach combines technical expertise with practical business understanding, ensuring you receive advice that's both compliant and commercially sound.
                </p>
                <div className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-[14px] font-semibold">Year-round support</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-[14px] font-semibold">Dedicated tax specialists</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-[14px] font-semibold">Proven track record</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div ref={ctaRef} className={`text-center transition-all duration-500 delay-400 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-surface border border-border rounded-[var(--radius-sm)] p-8 md:p-12 shadow-soft">
            <h3 className="text-[28px] leading-[1.4] font-semibold tracking-[-0.2px] mb-4">
              Ready to Optimize Your Tax Position?
            </h3>
            <p className="text-[16px] leading-[1.6] text-muted-foreground mb-6 max-w-2xl mx-auto">
              Schedule a consultation with our tax experts to discuss your specific needs and discover how we can help you achieve significant tax savings.
            </p>
            <Link to="/team-and-contact#contact-us" className="inline-block">
              <Button size="lg" className="rounded-[var(--radius-full)] px-8">
                Contact Our Tax Team
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}